import RoundTab from "components/RoundTabButtons/RoundTab";
import moment from "moment-timezone";
import React, { useRef } from "react";
import { Views } from "react-big-calendar";
import { useDrag, useDragLayer, useDrop } from "react-dnd";
import { CommonGrid } from "styledComponents/common";
import {
  CalendarButtonText,
  CalendarIcon,
  CalendarIconButton,
  CalendarLegend,
  CalendarToolbarContainer,
  DateCellWrapper,
  LegendDot,
  LegendItem,
  LegendSquare,
  MonthChangeIcon,
  MonthPickerContainer,
  NextPrevButton,
  TimeSlotCellWrapper,
  TimeSlotWrapper,
  UnscheduledToggleIcon,
  ViewButtonContainer,
} from "styledComponents/shiftAndSchedule";

import LeftArrow from "../../../assets/img/icons/arrow_left_gray.svg";
import RightArrow from "../../../assets/img/icons/arrow_right_gray.svg";
import calendarIcon from "../../../assets/img/icons/calender.svg";
import noAssigneeIcon from "../../../assets/img/icons/no-assignee.svg";
import { createNotification } from "../../../modules/notificationManager";
import { ScheduleRightContainerBody } from "../../../styledComponents/timesheet";
import { parseDateTimeInTimezone } from "../shiftAndScheduleHelper";

export const ShiftScheduleToolbar = ({
  goToBack,
  goToNext,
  view,
  onViewChange,
  selectedDate,
  onMapViewClick,
}) => {
  return (
    <CalendarToolbarContainer style={{ width: "100%", marginBottom: "0" }}>
      <ViewButtonContainer gap="5px">
        <RoundTab
          height="32px"
          text="Monthly"
          selected={view === "month"}
          onClick={() => onViewChange(Views.MONTH)}
          showIcon={false}
        />
        <RoundTab
          text="Weekly"
          selected={view === "week"}
          onClick={() => onViewChange(Views.WEEK)}
          showIcon={false}
          height="32px"
        />
        <RoundTab
          text="Daily"
          selected={view === "day"}
          onClick={() => onViewChange(Views.DAY)}
          showIcon={false}
          height="32px"
        />
        <RoundTab
          text="Map View"
          selected={false}
          onClick={onMapViewClick}
          showIcon={false}
          height="32px"
        />
      </ViewButtonContainer>

      <CommonGrid gap="4px">
        <CalendarIconButton>
          <CalendarIcon src={calendarIcon} alt="Calendar" />
          <CalendarButtonText fontSize="15px">
            {view === "month"
              ? moment(selectedDate).format("MMMM YYYY")
              : view === "week"
              ? `${moment(selectedDate)
                  .startOf("isoWeek")
                  .format("DD MMM")} - ${moment(selectedDate)
                  .endOf("isoWeek")
                  .format("DD MMM , YYYY")}`
              : moment(selectedDate).format("LL")}
          </CalendarButtonText>
        </CalendarIconButton>
        <MonthPickerContainer>
          <NextPrevButton onClick={goToBack}>
            <MonthChangeIcon src={LeftArrow} alt="" />
          </NextPrevButton>
          <NextPrevButton onClick={goToNext}>
            <MonthChangeIcon src={RightArrow} alt="" />
          </NextPrevButton>
        </MonthPickerContainer>
      </CommonGrid>
    </CalendarToolbarContainer>
  );
};

export const ShiftScheduleLegend = ({ selectedOrganization }) => {
  // Dynamic labels based on organization type (default to "operation" if not defined)
  const orgType = selectedOrganization?.type || "operation";
  const isOperationType = orgType === "operation";
  const itemTypeText = isOperationType ? "Task" : "Job";

  return (
    <div style={{ marginTop: "8px", alignSelf: "flex-start" }}>
      <CalendarLegend>
        <LegendItem bgColor="#edf8fe" borderColor="#b4c6f9">
          <LegendDot color="#2266EF" />
          {itemTypeText}
        </LegendItem>
        {isOperationType && (
          <LegendItem bgColor="#f4ecfe" borderColor="#c6b4f9">
            <LegendSquare color="#6C2EF2" />
            Shift
          </LegendItem>
        )}
      </CalendarLegend>
    </div>
  );
};

// Re-export for existing usage (e.g. arrow icon in unscheduled header)
export { UnscheduledToggleIcon };

// Draggable weekly grid event card (shared)
export const WeeklyGridEventCard = ({
  event,
  onSelect,
  timeText,
  dragType = "CALENDAR_EVENT",
  selectedOrganization,
}) => {
  const didDragRef = useRef(false);

  // Check if event has already started
  const isEventPast = React.useMemo(() => {
    if (!event?.start_time) return false;
    const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
    const eventStart = parseDateTimeInTimezone(event.start_time, orgTimezone);
    if (!eventStart) return false;
    const now = moment.tz(orgTimezone);
    return eventStart.isBefore(now);
  }, [event?.start_time, selectedOrganization?.timezone]);

  const isJob = Boolean(event?.work_order?.id);
  const isOffice = Boolean(event?.office);

  // Job schedules and office schedules represent assignees differently.
  const hasNoAssignee = isJob
    ? !(
        event?.assignee_id ||
        event?.assignee?.id ||
        event?.assignee ||
        event?.assigned_to_id ||
        event?.assigned_to?.id ||
        event?.assigned_to ||
        event?.user_id ||
        event?.user?.id ||
        (Array.isArray(event?.assignees) && event.assignees.length > 0)
      )
    : isOffice
    ? !(Array.isArray(event?.assignees) && event.assignees.length > 0)
    : false;

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: dragType,
      item: { eventId: event?.id },
      canDrag: () => {
        return Boolean(event?.id);
      },
      collect: monitor => {
        const dragging = monitor.isDragging();
        if (dragging) didDragRef.current = true;
        return { isDragging: dragging };
      },
      end: () => {
        setTimeout(() => {
          didDragRef.current = false;
        }, 0);
      },
    }),
    [event?.id, dragType, isEventPast],
  );

  // Prioritize theme_color if provided, otherwise use defaults based on type
  const bg = event?.theme_color
    ? event.theme_color
    : isOffice
    ? "#f4ecfe"
    : isJob
    ? "#edf8fe"
    : "#D2E4FE";
  const border = isOffice ? "#c6b4f9" : isJob ? "#b4c6f9" : "#b4c6f9";
  const dot = isOffice ? "#6C2EF2" : "#2266EF";

  const eventName = event?.name || event?.title || "";

  return (
    <div
      ref={drag}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0px",
        padding: "4px 8px",
        borderRadius: "4px",
        border: `1px solid ${border}`,
        background: bg,
        cursor: isDragging ? "grabbing" : "grab",
        opacity: isDragging ? 0.5 : 1,
        userSelect: "none",
        height: "42px",
        justifyContent: "center",
        boxSizing: "border-box",
      }}
      onMouseDown={e => {
        e.stopPropagation();
        didDragRef.current = false;
      }}
      onClick={e => {
        if (didDragRef.current) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (typeof onSelect === "function") onSelect(event);
      }}
    >
      {timeText ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            height: "17px",
            width: "100%",
          }}
        >
          <span
            style={{
              width: "8px",
              height: "8px",
              borderRadius: isOffice ? "2px" : "50%", // Square for shifts, circle for jobs
              background: dot,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontFamily: '"Mona Sans", sans-serif',
              fontSize: "12px",
              fontWeight: 600,
              lineHeight: "16.92px",
              color: "#000e2a",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
              flex: 1,
            }}
          >
            {timeText}
          </span>
          {hasNoAssignee && (
            <img
              src={noAssigneeIcon}
              alt="No assignee"
              style={{
                width: "12px",
                height: "12px",
                flexShrink: 0,
              }}
              title="No assignee"
            />
          )}
        </div>
      ) : null}
      <div
        style={{
          height: "17px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <span
          style={{
            fontFamily: '"Mona Sans", sans-serif',
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "16.92px",
            color: "#353a46",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flex: 1,
          }}
          title={eventName}
        >
          {eventName}
        </span>
        {hasNoAssignee && !timeText && (
          <img
            src={noAssigneeIcon}
            alt="No assignee"
            style={{
              width: "12px",
              height: "12px",
              flexShrink: 0,
            }}
            title="No assignee"
          />
        )}
      </div>
    </div>
  );
};

// Adds a global body class during dragging to tweak pointer-events
export const DndBodyClassOnDrag = ({ dragItemTypes }) => {
  const { isDraggingAny, itemType } = useDragLayer(monitor => ({
    isDraggingAny: monitor.isDragging(),
    itemType: monitor.getItemType(),
  }));

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const cls = "rbc-dnd-dragging";
    let t;
    const matchesType =
      !dragItemTypes ||
      (Array.isArray(dragItemTypes) && dragItemTypes.includes(itemType));
    if (isDraggingAny && matchesType) {
      t = setTimeout(() => document.body.classList.add(cls), 0);
    } else {
      document.body.classList.remove(cls);
    }
    return () => {
      if (t) clearTimeout(t);
      document.body.classList.remove(cls);
    };
  }, [isDraggingAny, itemType, dragItemTypes]);

  return null;
};

// Drag and drop types constant
export const DRAG_TYPES = {
  JOB_ITEM: "JOB_ITEM",
  CALENDAR_EVENT: "CALENDAR_EVENT",
};

// Debug helper for drag and drop
const isShiftScheduleDndDebugEnabled = () => {
  try {
    // Always log on localhost during development so debugging is straightforward.
    // You can still disable by setting localStorage debugShiftScheduleDnd="0".
    if (typeof window === "undefined") return false;
    const ls = window?.localStorage?.getItem("debugShiftScheduleDnd");
    if (ls === "0") return false;
    if (ls === "1") return true;
    const host = window?.location?.hostname;
    return host === "localhost" || host === "127.0.0.1";
  } catch (_e) {
    return false;
  }
};

// Time Column Wrapper - adds hover feedback for `.rbc-day-slot.rbc-time-column`
// Actual scheduling drop is still handled by TimeSlotCellWrapperComponent (per cell)
export const TimeColumnWrapperComponent = ({ children }) => {
  const [{ isOver }, drop] = useDrop({
    accept: [DRAG_TYPES.JOB_ITEM, DRAG_TYPES.CALENDAR_EVENT],
    drop: (item, monitor) => {
      // Allow the inner time-slot drop targets to handle the real drop payload.
      // This wrapper is only for visual feedback.
      if (monitor.didDrop()) return;
      return;
    },
    collect: monitor => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  });

  if (!React.isValidElement(children)) return children;

  const overStyle = isOver
    ? {
        outline: "2px dashed #2268EF",
        outlineOffset: "-2px",
        background: "#f0f0f0",
      }
    : {};

  return React.cloneElement(children, {
    ref: node => drop(node),
    style: { ...(children.props?.style || {}), ...overStyle },
  });
};

// Individual Time Slot Cell Wrapper - Makes each date cell within a time slot row droppable
export const TimeSlotCellWrapperComponent = ({
  children,
  slotTime,
  cellDate,
  unscheduledJobsList,
  selectedOrganization,
  findJobFromUnscheduledList,
  findObjectById,
  validateFutureDate,
  createTimeSlotPayload,
  determineType,
  updateJobRequest,
  editShift,
  officeShiftsList,
  jobShiftsList,
  setShiftsList,
  setUnscheduledJobsList,
  setIsEdited,
  trackOptimisticUpdate,
  calendarStep = 30,
}) => {
  // In week/day views, react-big-calendar provides `slotTime` as the full datetime for the slot.
  // Use it directly to avoid day-offset bugs from trying to recompute the column date.
  const slotStart = slotTime
    ? moment(slotTime).second(0).millisecond(0).toDate()
    : slotTime && cellDate
    ? moment(cellDate)
        .hour(moment(slotTime).hour())
        .minute(moment(slotTime).minute())
        .second(0)
        .millisecond(0)
        .toDate()
    : null;
  const defaultSlotEnd = slotStart
    ? moment(slotStart).add(calendarStep, "minutes").toDate()
    : null;

  // Check if time slot has already passed
  const isSlotPast = React.useMemo(() => {
    if (!slotStart) return false;
    const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
    const slotMoment = moment.tz(slotStart, orgTimezone);
    const now = moment.tz(orgTimezone);
    return slotMoment.isBefore(now);
  }, [slotStart, selectedOrganization?.timezone]);

  const [{ isOver }, drop] = useDrop({
    accept: [DRAG_TYPES.JOB_ITEM, DRAG_TYPES.CALENDAR_EVENT],
    canDrop: () => {
      // Disable dropping if time slot has already passed
      return !isSlotPast;
    },
    drop: (item, monitor) => {
      // Double check - don't allow drop if slot has passed
      if (isSlotPast) {
        createNotification("error", "Cannot schedule in the past", 3000);
        return;
      }
      if (isShiftScheduleDndDebugEnabled()) {
        const childProps = React.isValidElement(children) ? children.props : {};
        // eslint-disable-next-line no-console
        // console.log("[ShiftSchedule DND] DROP on time-slot cell", {
        //   item,
        //   slotTime,
        //   cellDate,
        //   computed: {
        //     slotStart,
        //     slotEnd: defaultSlotEnd,
        //     slotStartISO: slotStart ? moment(slotStart).format() : null,
        //     slotEndISO: defaultSlotEnd ? moment(defaultSlotEnd).format() : null,
        //   },
        //   targetCellProps: {
        //     value: childProps?.value,
        //     date: childProps?.date,
        //     dataDate: childProps?.["data-date"],
        //     className: childProps?.className,
        //   },
        // });
      }

      if (!slotStart || !defaultSlotEnd) {
        createNotification("error", "Invalid time slot selected", 3000);
        return;
      }

      // Handle Calendar Event → Time Slot (reschedule event to new time slot)
      if (item.eventId) {
        const eventId = item.eventId;

        // Find the full object from ShiftsList
        const fullObject = findObjectById(
          eventId,
          officeShiftsList,
          jobShiftsList,
        );
        if (!fullObject) {
          createNotification("error", "Event not found", 3000);
          return;
        }

        // Block moving past events (API rejects "past job not movable")
        const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
        const existingStart = fullObject.start_time
          ? parseDateTimeInTimezone(fullObject.start_time, orgTimezone)
          : null;
        if (existingStart && existingStart.isBefore(moment.tz(orgTimezone))) {
          createNotification("error", "Past job/shift cannot be moved", 3000);
          return;
        }

        // Validate future date
        const validation = validateFutureDate(slotStart);
        if (!validation.isValid) {
          createNotification("error", validation.error, 3000);
          return;
        }

        // Determine type
        const type = determineType(fullObject);

        // If rescheduling a scheduled JOB, do NOT send required_* fields.
        // Only include required_* when scheduling from unscheduled list.
        let payload = null;
        if (type === "job") {
          const existingEnd = fullObject.end_time
            ? parseDateTimeInTimezone(fullObject.end_time, orgTimezone)
            : null;
          const durationMinutes =
            existingStart && existingEnd
              ? Math.max(1, existingEnd.diff(existingStart, "minutes"))
              : calendarStep;
          const slotEnd = moment(slotStart)
            .add(durationMinutes, "minutes")
            .toDate();

          payload = {
            organization_id: selectedOrganization.id,
            workOrder_id: fullObject.work_order.id,
            job_id: fullObject.id,
            status: "scheduled",
            start_time: moment
              .tz(slotStart, orgTimezone)
              .format("YYYY-MM-DDTHH:mm:ss"),
            end_time: moment
              .tz(slotEnd, orgTimezone)
              .format("YYYY-MM-DDTHH:mm:ss"),
            is_anytime: false,
          };
        } else if (type === "office") {
          payload = createTimeSlotPayload(
            fullObject,
            slotStart,
            defaultSlotEnd,
            selectedOrganization,
          );
        }

        if (payload) {
          // Optimistic UI: move the event immediately in the calendar
          if (setShiftsList && typeof setShiftsList === "function") {
            const uiStart = moment.tz(slotStart, orgTimezone).format();
            // For office shifts, use payload.end_time if available, otherwise calculate from duration
            let uiEnd;
            if (type === "office" && payload.end_time) {
              uiEnd = moment
                .tz(payload.end_time, "YYYY-MM-DDTHH:mm:ss", orgTimezone)
                .format();
            } else if (type === "job" && payload.end_time) {
              uiEnd = moment
                .tz(payload.end_time, "YYYY-MM-DDTHH:mm:ss", orgTimezone)
                .format();
            } else {
              // Fallback to defaultSlotEnd
              uiEnd = moment.tz(defaultSlotEnd, orgTimezone).format();
            }

            const optimisticState = {
              ...fullObject,
              start_time: uiStart,
              end_time: uiEnd,
              is_anytime: type === "office" ? fullObject.is_anytime : false,
              status: "scheduled",
            };

            // Track the optimistic update
            if (trackOptimisticUpdate) {
              trackOptimisticUpdate(
                eventId,
                fullObject,
                optimisticState,
                "reschedule",
                type,
              );
            }

            // Update the event in place - this removes it from old location and adds to new location
            setShiftsList(prev =>
              Array.isArray(prev)
                ? prev.map(e => (e.id === eventId ? optimisticState : e))
                : prev,
            );
          }

          // Call API for office shifts
          if (type === "office" && editShift) {
            editShift(payload);
            setIsEdited(true);
          }
          // Call API for jobs
          else if (type === "job" && updateJobRequest) {
            updateJobRequest(payload);
            setIsEdited(true);
          }
        } else {
          createNotification("error", "Failed to reschedule event", 3000);
        }

        return;
      }

      // Handle Unscheduled Job → Time Slot
      if (item.jobId) {
        const jobId = item.jobId;
        if (!jobId) {
          createNotification("error", "Job not found", 3000);
          return;
        }

        // Find the full object from unscheduled list
        const fullObject = findJobFromUnscheduledList(
          jobId,
          unscheduledJobsList,
        );
        if (!fullObject) {
          createNotification(
            "error",
            "Job not found in unscheduled list",
            3000,
          );
          return;
        }

        // Validate future date
        const validation = validateFutureDate(slotStart);
        if (!validation.isValid) {
          createNotification("error", validation.error, 3000);
          return;
        }

        // Determine type (should be job)
        const type = determineType(fullObject);
        if (type !== "job") {
          createNotification("error", "Invalid job type", 3000);
          return;
        }

        // Create time slot payload
        const payload = createTimeSlotPayload(
          fullObject,
          slotStart,
          defaultSlotEnd,
          selectedOrganization,
        );

        if (payload) {
          const orgTimezone =
            selectedOrganization?.timezone || moment.tz.guess();
          const uiStart = moment.tz(slotStart, orgTimezone).format();
          const uiEnd = moment.tz(defaultSlotEnd, orgTimezone).format();

          const optimisticState = {
            ...fullObject,
            start_time: uiStart,
            end_time: uiEnd,
            is_anytime: false,
            status: "scheduled",
          };

          // Track the optimistic update
          if (trackOptimisticUpdate) {
            trackOptimisticUpdate(
              fullObject.id,
              { ...fullObject, status: "unscheduled" },
              optimisticState,
              "schedule",
              type,
            );
          }

          // Optimistic UI: remove from unscheduled list immediately
          if (
            setUnscheduledJobsList &&
            typeof setUnscheduledJobsList === "function"
          ) {
            setUnscheduledJobsList(prev =>
              Array.isArray(prev) ? prev.filter(j => j.id !== jobId) : prev,
            );
          }
          // Optimistic UI: add/update in calendar immediately
          if (setShiftsList && typeof setShiftsList === "function") {
            setShiftsList(prev => {
              const next = Array.isArray(prev) ? [...prev] : [];
              const idx = next.findIndex(e => e.id === fullObject.id);
              if (idx >= 0) next[idx] = { ...next[idx], ...optimisticState };
              else next.push(optimisticState);
              return next;
            });
          }

          // Call API for jobs - data will be refreshed from API after successful update
          if (type === "job" && updateJobRequest) {
            updateJobRequest(payload);
            setIsEdited(true);
          }
        } else {
          createNotification("error", "Failed to schedule job", 3000);
        }
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
    }),
  });

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const cls = "rbc-dnd-over-invalid";
    if (isOver && isSlotPast) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [isOver, isSlotPast]);

  if (!React.isValidElement(children)) return children;

  // Use outline (not border) so we don't change the slot's layout/width/height.
  const overStyle =
    isOver && !isSlotPast
      ? {
          outline: "2px dashed #2268EF",
          outlineOffset: "-2px",
          background: "#f0f0f0",
        }
      : {};

  const className = isOver && isSlotPast ? "rbc-drop-invalid" : "";

  return React.cloneElement(children, {
    ref: node => drop(node),
    style: { ...(children.props?.style || {}), ...overStyle },
    "data-past": isSlotPast ? "true" : "false",
    className: className
      ? (children.props?.className || "") + " " + className
      : children.props?.className || "",
  });
};

// Time Slot Wrapper Component - Makes each time slot row a container that wraps individual date cells
export const TimeSlotWrapperComponent = ({
  value,
  children,
  resource,
  unscheduledJobsList,
  selectedOrganization,
  findJobFromUnscheduledList,
  findObjectById,
  validateFutureDate,
  createTimeSlotPayload,
  determineType,
  updateJobRequest,
  editShift,
  officeShiftsList,
  jobShiftsList,
  setShiftsList,
  setUnscheduledJobsList,
  setIsEdited,
  trackOptimisticUpdate,
  calendarStep = 30, // Default step is 30 minutes
  view, // Current view (week/day)
  selectedDate, // Selected date for calculating week dates
}) => {
  // Convert the slot value to a proper date object
  // react-big-calendar provides value as a Date object representing the slot start time
  const slotTime = value ? moment(value).toDate() : null;

  // In week view, `children` often includes a gutter cell in addition to the 7 day columns.
  // The raw React child index is not the day index. Count only actual `.rbc-time-slot` day cells.
  let weekDayIndex = -1;
  const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
  const childCount = React.Children.count(children);
  const hasWeekGutter = view === "week" && childCount > 7;

  // Wrap each child (date cell) with a drop target
  // In week view, children contains multiple date cells, each needs its own drop target
  const wrappedChildren = React.Children.map(children, (child, index) => {
    if (!child || !slotTime) {
      return child;
    }

    if (!React.isValidElement(child)) return child;

    const childClassName =
      typeof child.props?.className === "string" ? child.props.className : "";

    if (view === "week") {
      // Only count actual day cells (time slots). Skip gutter/labels.
      // In many RBC builds, the first child is the time gutter slot (also `.rbc-time-slot`),
      // which would shift all day columns by -1 if counted.
      if (hasWeekGutter && index === 0) {
        return child;
      }
      if (!childClassName.includes("rbc-time-slot")) {
        return child;
      }
      weekDayIndex += 1;
    }

    // Extract date from child's props or calculate based on view
    let cellDate = null;

    // Try multiple ways to get the date from child's props
    // react-big-calendar might pass date in different prop names
    if (child.props) {
      if (child.props.value) {
        cellDate = moment(child.props.value).toDate();
      } else if (child.props.date) {
        cellDate = moment(child.props.date).toDate();
      } else if (
        child.props.children &&
        React.Children.count(child.props.children) > 0
      ) {
        // Try to get date from nested children
        const firstChild = React.Children.toArray(child.props.children)[0];
        if (firstChild && firstChild.props && firstChild.props.value) {
          cellDate = moment(firstChild.props.value).toDate();
        }
      }
    }

    // If still no date found, calculate based on view
    if (!cellDate) {
      if (view === "week" && selectedDate) {
        // Calculate date for week view: start of week (in org timezone) + weekDayIndex
        const weekStart = moment
          .tz(selectedDate, "YYYY-MM-DD", orgTimezone)
          .startOf("week")
          .startOf("day");
        const dayOffset = weekDayIndex >= 0 ? weekDayIndex : index;
        cellDate = weekStart.clone().add(dayOffset, "days").toDate();
      } else if (view === "day" && selectedDate) {
        // For day view, use the selected date
        cellDate = moment.tz(selectedDate, "YYYY-MM-DD", orgTimezone).toDate();
      } else {
        // Fallback: use slotTime's date part
        cellDate = moment(slotTime).startOf("day").toDate();
      }
    }

    return (
      <TimeSlotCellWrapperComponent
        key={child.key || index}
        slotTime={slotTime}
        cellDate={cellDate}
        unscheduledJobsList={unscheduledJobsList}
        selectedOrganization={selectedOrganization}
        findJobFromUnscheduledList={findJobFromUnscheduledList}
        findObjectById={findObjectById}
        validateFutureDate={validateFutureDate}
        createTimeSlotPayload={createTimeSlotPayload}
        determineType={determineType}
        updateJobRequest={updateJobRequest}
        editShift={editShift}
        officeShiftsList={officeShiftsList}
        jobShiftsList={jobShiftsList}
        setShiftsList={setShiftsList}
        setUnscheduledJobsList={setUnscheduledJobsList}
        setIsEdited={setIsEdited}
        trackOptimisticUpdate={trackOptimisticUpdate}
        calendarStep={calendarStep}
      >
        {child}
      </TimeSlotCellWrapperComponent>
    );
  });

  return <>{wrappedChildren}</>;
};

// Date Cell Wrapper Component - Makes each date cell a drop target
export const DateCellWrapperComponent = ({
  value,
  children,
  view,
  timeZone,
  allEvents,
  onShowMore,
  unscheduledJobsList,
  selectedOrganization,
  findJobFromUnscheduledList,
  findObjectById,
  validateFutureDate,
  createSchedulePayload,
  createReschedulePayload,
  determineType,
  updateJobRequest,
  editShift,
  officeShiftsList,
  jobShiftsList,
  setShiftsList,
  setUnscheduledJobsList,
  setIsEdited,
  trackOptimisticUpdate,
  onUnscheduledJobDropped,
}) => {
  // Convert the date value to a proper date object
  const cellDate = value ? moment(value).toDate() : null;
  const isToday = cellDate ? moment(cellDate).isSame(moment(), "day") : false;

  // Check if date is in the past (before today)
  const isDatePast = React.useMemo(() => {
    if (!cellDate) return false;
    const orgTimezone =
      selectedOrganization?.timezone || timeZone || moment.tz.guess();
    const cellMoment = moment.tz(cellDate, orgTimezone).startOf("day");
    const today = moment.tz(orgTimezone).startOf("day");
    return cellMoment.isBefore(today);
  }, [cellDate, selectedOrganization?.timezone, timeZone]);

  const [{ isOver }, drop] = useDrop({
    accept: [DRAG_TYPES.JOB_ITEM, DRAG_TYPES.CALENDAR_EVENT],
    canDrop: () => {
      // Disable dropping if date is in the past (before today)
      return !isDatePast;
    },
    drop: (item, monitor) => {
      if (isShiftScheduleDndDebugEnabled()) {
        const childProps = React.isValidElement(children) ? children.props : {};
        // eslint-disable-next-line no-console
        // console.log("[ShiftSchedule DND] DROP on month date cell", {
        //   item,
        //   cellDate,
        //   cellDateISO: cellDate ? moment(cellDate).format() : null,
        //   targetCellProps: {
        //     value: childProps?.value,
        //     date: childProps?.date,
        //     dataDate: childProps?.["data-date"],
        //     className: childProps?.className,
        //   },
        // });
      }

      if (!cellDate) {
        createNotification("error", "Invalid date selected", 3000);
        return;
      }

      // Double check - don't allow drop if date is in the past
      if (isDatePast) {
        createNotification("error", "Cannot schedule in the past", 3000);
        return;
      }

      // Handle Calendar Event → Date Cell (reschedule event to new date)
      if (item.eventId) {
        const eventId = item.eventId;

        // Find the full object from ShiftsList
        const fullObject = findObjectById(
          eventId,
          officeShiftsList,
          jobShiftsList,
        );
        if (!fullObject) {
          createNotification("error", "Event not found", 3000);
          return;
        }

        // Block moving past events (API rejects "past job not movable")
        const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
        const existingStart = fullObject.start_time
          ? parseDateTimeInTimezone(fullObject.start_time, orgTimezone)
          : null;
        if (existingStart && existingStart.isBefore(moment.tz(orgTimezone))) {
          createNotification("error", "Past job/shift cannot be moved", 3000);
          return;
        }

        // Store original event data for potential revert
        const originalEvent = {
          ...fullObject,
          start_time: fullObject.start_time,
          end_time: fullObject.end_time,
        };

        // Validate future date
        const validation = validateFutureDate(cellDate);
        if (!validation.isValid) {
          createNotification("error", validation.error, 3000);
          return;
        }

        // Determine type
        const type = determineType(fullObject);

        // Month view drop behavior:
        // - Scheduled Jobs: preserve time-of-day (and duration) and only change the DATE.
        //   Do NOT include required_* (only for unscheduled->calendar).
        // - Anytime Jobs: keep whole-day anytime.
        // - Office: keep existing office behavior (office API expects required_*).
        const dropDateInTz = moment.tz(cellDate, orgTimezone);
        let payload = null;
        if (type === "job") {
          if (fullObject.is_anytime === true) {
            // Preserve anytime behavior as whole-day
            payload = {
              organization_id: selectedOrganization.id,
              workOrder_id: fullObject.work_order.id,
              job_id: fullObject.id,
              status: "scheduled",
              start_time: dropDateInTz
                .clone()
                .startOf("day")
                .format("YYYY-MM-DDTHH:mm:ss"),
              end_time: dropDateInTz
                .clone()
                .endOf("day")
                .format("YYYY-MM-DDTHH:mm:ss"),
              is_anytime: true,
            };
          } else {
            const existingStart = fullObject.start_time
              ? parseDateTimeInTimezone(fullObject.start_time, orgTimezone)
              : null;
            const existingEnd = fullObject.end_time
              ? parseDateTimeInTimezone(fullObject.end_time, orgTimezone)
              : null;

            if (!existingStart || !existingEnd) {
              createNotification(
                "error",
                "Invalid job time. Cannot reschedule.",
                3000,
              );
              return;
            }

            const durationMinutes = Math.max(
              1,
              existingEnd.diff(existingStart, "minutes"),
            );

            const newStart = dropDateInTz
              .clone()
              .hour(existingStart.hour())
              .minute(existingStart.minute())
              .second(existingStart.second())
              .millisecond(0);
            const newEnd = newStart.clone().add(durationMinutes, "minutes");

            payload = {
              organization_id: selectedOrganization.id,
              workOrder_id: fullObject.work_order.id,
              job_id: fullObject.id,
              status: "scheduled",
              start_time: newStart.format("YYYY-MM-DDTHH:mm:ss"),
              end_time: newEnd.format("YYYY-MM-DDTHH:mm:ss"),
              is_anytime: false,
            };
          }
        } else if (type === "office") {
          // Pass original fullObject so helper can check is_anytime and preserve time
          payload = createReschedulePayload(
            fullObject,
            cellDate,
            selectedOrganization,
          );
          // Don't overwrite - helper already sets start_time/end_time correctly
          // based on is_anytime flag and preserves time when is_anytime === false
        }

        if (payload) {
          // Update state immediately for visual feedback
          // Handle time based on is_anytime flag using timezone-aware operations
          const orgTimezone =
            selectedOrganization?.timezone || moment.tz.guess();
          let newStartTime, newEndTime;

          // For office shifts, use payload times directly if available (ensures consistency)
          // For jobs, calculate from fullObject to preserve time components
          if (type === "office" && payload.start_time && payload.end_time) {
            // Use payload times directly for office shifts
            newStartTime = moment
              .tz(payload.start_time, "YYYY-MM-DDTHH:mm:ss", orgTimezone)
              .format();
            newEndTime = moment
              .tz(payload.end_time, "YYYY-MM-DDTHH:mm:ss", orgTimezone)
              .format();
          } else if (
            fullObject.is_anytime === false &&
            fullObject.start_time &&
            fullObject.end_time
          ) {
            // Preserve existing times but update dates using timezone-aware operations
            const existingStartTime = parseDateTimeInTimezone(
              fullObject.start_time,
              orgTimezone,
            );
            const existingEndTime = parseDateTimeInTimezone(
              fullObject.end_time,
              orgTimezone,
            );

            if (existingStartTime && existingEndTime) {
              // Extract time components
              const startHour = existingStartTime.hour();
              const startMinute = existingStartTime.minute();
              const startSecond = existingStartTime.second();
              const endHour = existingEndTime.hour();
              const endMinute = existingEndTime.minute();
              const endSecond = existingEndTime.second();

              // Apply to new cell date in organization timezone
              const cellDateInTz = moment.tz(cellDate, orgTimezone);
              newStartTime = moment
                .tz(
                  cellDateInTz.format("YYYY-MM-DD") +
                    ` ${String(startHour).padStart(2, "0")}:${String(
                      startMinute,
                    ).padStart(2, "0")}:${String(startSecond).padStart(
                      2,
                      "0",
                    )}`,
                  "YYYY-MM-DD HH:mm:ss",
                  orgTimezone,
                )
                .format();
              newEndTime = moment
                .tz(
                  cellDateInTz.format("YYYY-MM-DD") +
                    ` ${String(endHour).padStart(2, "0")}:${String(
                      endMinute,
                    ).padStart(2, "0")}:${String(endSecond).padStart(2, "0")}`,
                  "YYYY-MM-DD HH:mm:ss",
                  orgTimezone,
                )
                .format();
            } else {
              // Fallback if parsing fails
              newStartTime = moment(cellDate)
                .hour(moment(fullObject.start_time).hour())
                .minute(moment(fullObject.start_time).minute())
                .second(moment(fullObject.start_time).second())
                .format();
              newEndTime = moment(cellDate)
                .hour(moment(fullObject.end_time).hour())
                .minute(moment(fullObject.end_time).minute())
                .second(moment(fullObject.end_time).second())
                .format();
            }
          } else {
            // Set to whole day
            newStartTime = moment(cellDate).startOf("day").format();
            newEndTime = moment(cellDate).endOf("day").format();
          }

          // Optimistic UI: update the event immediately in the calendar list so
          // "Show more" panel also reflects the move/removal.
          if (setShiftsList && typeof setShiftsList === "function") {
            const optimisticState = {
              ...fullObject,
              start_time: newStartTime,
              end_time: newEndTime,
              // For office shifts, preserve is_anytime from original object (helper handles it)
              // For jobs, use payload.is_anytime if available, otherwise keep existing
              is_anytime:
                type === "office"
                  ? fullObject.is_anytime
                  : typeof payload.is_anytime === "boolean"
                  ? payload.is_anytime
                  : fullObject.is_anytime,
              status: "scheduled",
            };

            // Track the optimistic update
            if (trackOptimisticUpdate) {
              trackOptimisticUpdate(
                eventId,
                fullObject,
                optimisticState,
                "reschedule",
                type,
              );
            }

            setShiftsList(prev =>
              Array.isArray(prev)
                ? prev.map(e => (e.id === eventId ? optimisticState : e))
                : prev,
            );
          }

          // Call API for office shifts
          if (type === "office" && editShift) {
            editShift(payload);
            setIsEdited(true);
          }
          // Call API for jobs
          else if (type === "job" && updateJobRequest) {
            updateJobRequest(payload);
            setIsEdited(true);
          }
        } else {
          createNotification("error", "Failed to reschedule event", 3000);
        }

        return;
      }

      // Handle Unscheduled Job → Date Cell
      if (item.jobId) {
        const jobId = item.jobId;
        if (!jobId) {
          createNotification("error", "Job not found", 3000);
          return;
        }

        // Find the full object from unscheduled list
        const fullObject = findJobFromUnscheduledList(
          jobId,
          unscheduledJobsList,
        );
        if (!fullObject) {
          createNotification(
            "error",
            "Job not found in unscheduled list",
            3000,
          );
          return;
        }

        // Validate future date
        const validation = validateFutureDate(cellDate);
        if (!validation.isValid) {
          createNotification("error", validation.error, 3000);
          return;
        }

        // Determine type (should be job)
        const type = determineType(fullObject);
        if (type !== "job") {
          createNotification("error", "Invalid job type", 3000);
          return;
        }

        // If a callback is provided (month view), open the time range modal
        // instead of dispatching directly
        if (typeof onUnscheduledJobDropped === "function") {
          onUnscheduledJobDropped(fullObject, cellDate);
          return;
        }

        // Fallback: dispatch directly (legacy behavior)
        const payload = createSchedulePayload(
          fullObject,
          cellDate,
          selectedOrganization,
        );

        if (payload) {
          // Optimistic UI: remove from unscheduled list immediately and add to calendar immediately
          const orgTimezone =
            selectedOrganization?.timezone || moment.tz.guess();
          const uiStart = moment
            .tz(payload.start_time, "YYYY-MM-DDTHH:mm:ss", orgTimezone)
            .format();
          const uiEnd = moment
            .tz(payload.end_time, "YYYY-MM-DDTHH:mm:ss", orgTimezone)
            .format();

          const optimisticState = {
            ...fullObject,
            start_time: uiStart,
            end_time: uiEnd,
            is_anytime: true,
            status: "scheduled",
          };

          // Track the optimistic update
          if (trackOptimisticUpdate) {
            trackOptimisticUpdate(
              fullObject.id,
              { ...fullObject, status: "unscheduled" },
              optimisticState,
              "schedule",
              type,
            );
          }

          if (
            setUnscheduledJobsList &&
            typeof setUnscheduledJobsList === "function"
          ) {
            setUnscheduledJobsList(prev =>
              Array.isArray(prev) ? prev.filter(j => j.id !== jobId) : prev,
            );
          }
          if (setShiftsList && typeof setShiftsList === "function") {
            setShiftsList(prev => {
              const next = Array.isArray(prev) ? [...prev] : [];
              const idx = next.findIndex(e => e.id === fullObject.id);
              if (idx >= 0) next[idx] = { ...next[idx], ...optimisticState };
              else next.push(optimisticState);
              return next;
            });
          }

          // Call API for jobs - data will be refreshed from API after successful update
          if (type === "job" && updateJobRequest) {
            updateJobRequest(payload);
            setIsEdited(true);
          }
        } else {
          createNotification("error", "Failed to schedule job", 3000);
        }
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
    }),
  });

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const cls = "rbc-dnd-over-invalid";
    if (isOver && isDatePast) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [isOver, isDatePast]);

  return (
    <DateCellWrapper
      ref={drop}
      isOver={isOver && !isDatePast}
      variant={view}
      isToday={isToday}
      data-past={isDatePast ? "true" : "false"}
      className={isOver && isDatePast ? "rbc-drop-invalid" : ""}
    >
      {children}
      {view === "month" && cellDate && Array.isArray(allEvents) && onShowMore
        ? (() => {
            const dayKey = moment(cellDate).tz(timeZone).format("YYYY-MM-DD");
            const eventsForDay = allEvents.filter(ev => {
              const evDay = parseDateTimeInTimezone(
                ev?.start_time,
                timeZone,
              )?.format("YYYY-MM-DD");
              return evDay === dayKey;
            });

            // Keep ordering stable and aligned with month cell display: all-day first, then by start time.
            const sortedEventsForDay = [...eventsForDay].sort((a, b) => {
              const aAllDay = a?.is_anytime === true ? 1 : 0;
              const bAllDay = b?.is_anytime === true ? 1 : 0;
              if (aAllDay !== bAllDay) return bAllDay - aAllDay;

              const aStart = parseDateTimeInTimezone(a?.start_time, timeZone);
              const bStart = parseDateTimeInTimezone(b?.start_time, timeZone);
              const aTs = aStart ? aStart.valueOf() : 0;
              const bTs = bStart ? bStart.valueOf() : 0;
              if (aTs !== bTs) return aTs - bTs;

              // Final tie-breaker
              const aId = a?.id ?? 0;
              const bId = b?.id ?? 0;
              return aId > bId ? 1 : aId < bId ? -1 : 0;
            });

            // Month cell shows at most 2 events; show-more should list ONLY the hidden (overflow) ones.
            const overflowEvents = sortedEventsForDay.slice(2);
            const overflowCount = overflowEvents.length;
            if (overflowCount <= 0) return null;

            return (
              <a
                href="#"
                className="schedule-month-show-more-corner"
                onMouseDown={e => {
                  // react-big-calendar starts "select slot" on mousedown; block it so
                  // clicking "+ more" does NOT open the create schedule modal.
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onTouchStart={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onShowMore(overflowEvents, cellDate);
                }}
                aria-label={`Show ${overflowCount} more events`}
              >
                +{overflowCount} more
              </a>
            );
          })()
        : null}
    </DateCellWrapper>
  );
};

// Unscheduled list drop zone wrapper component
export const UnscheduledDropZone = ({
  children,
  onDrop,
  ShiftsList,
  selectedOrganization,
  findObjectById,
  officeShiftsList,
  jobShiftsList,
  createUnscheduledPayload,
  determineType,
  updateJobRequest,
  setShiftsList,
  setUnscheduledJobsList,
  setIsEdited,
  trackOptimisticUpdate,
  className,
  style,
  view,
}) => {
  const [{ isOver }, drop] = useDrop({
    accept: DRAG_TYPES.CALENDAR_EVENT,
    drop: (item, monitor) => {
      // Scenario 2: Calendar Event → Unscheduled List
      const eventId = item.eventId;
      if (!eventId) {
        createNotification("error", "Event not found", 3000);
        return;
      }

      // Find the full object from ShiftsList (or search both lists)
      let fullObject = null;
      if (ShiftsList && Array.isArray(ShiftsList)) {
        fullObject = ShiftsList.find(item => item.id === eventId);
      }

      // If not found in ShiftsList, search in individual lists
      if (!fullObject) {
        fullObject = findObjectById(eventId, officeShiftsList, jobShiftsList);
      }

      if (!fullObject) {
        createNotification("error", "Event not found", 3000);
        return;
      }

      // Block moving past events to unscheduled (API rejects; UI must prevent).
      const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();
      const nowInTz = moment.tz(orgTimezone);
      const parsedStart = fullObject?.start_time
        ? parseDateTimeInTimezone(fullObject.start_time, orgTimezone)
        : null;
      const fallbackStart = fullObject?.start_time
        ? moment.tz(fullObject.start_time, orgTimezone)
        : null;
      const startMoment =
        parsedStart && parsedStart.isValid()
          ? parsedStart
          : fallbackStart && fallbackStart.isValid()
          ? fallbackStart
          : null;

      if (startMoment && startMoment.isBefore(nowInTz)) {
        createNotification("error", "Past job/shift cannot be moved", 3000);
        return;
      }

      // Determine type
      const type = determineType(fullObject);

      // Validate: Office schedules cannot be moved to unscheduled list
      if (type === "office") {
        createNotification(
          "error",
          "Team schedule cannot be moved to unscheduled list",
          3000,
        );
        return;
      }

      // Create unscheduled payload
      const payload = createUnscheduledPayload(
        fullObject,
        selectedOrganization,
      );

      if (payload) {
        const optimisticState = { ...fullObject, status: "unscheduled" };

        // Track the optimistic update
        if (trackOptimisticUpdate) {
          trackOptimisticUpdate(
            eventId,
            fullObject,
            optimisticState,
            "unschedule",
            type,
          );
        }

        // Optimistic UI: remove from calendar immediately and add to unscheduled list
        if (setShiftsList && typeof setShiftsList === "function") {
          setShiftsList(prev =>
            Array.isArray(prev) ? prev.filter(e => e.id !== eventId) : prev,
          );
        }
        if (
          setUnscheduledJobsList &&
          typeof setUnscheduledJobsList === "function"
        ) {
          setUnscheduledJobsList(prev => {
            const next = Array.isArray(prev) ? [...prev] : [];
            const exists = next.some(j => j.id === fullObject.id);
            if (!exists) next.unshift(optimisticState);
            return next;
          });
        }

        // Call API for jobs - data will be refreshed from API after successful update
        if (type === "job" && updateJobRequest) {
          // Add required fields for unscheduled job
          const jobPayload = {
            ...payload,
            is_anytime: false,
            recurring: false,
            bulk: false,
            start_time: null,
            end_time: null,
            required_start_date: null,
            required_end_date: null,
          };
          updateJobRequest(jobPayload);
          setIsEdited(true);
        }
      } else {
        createNotification(
          "error",
          "Failed to move event to unscheduled",
          3000,
        );
      }

      if (onDrop) onDrop(item, monitor);
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <ScheduleRightContainerBody
      ref={drop}
      isOver={isOver}
      className={className}
      style={style}
      view={view}
    >
      {children}
    </ScheduleRightContainerBody>
  );
};
